import { ApiError } from '../api';
import type { Dictionary } from '../i18n';
import * as Icon from './Icons';
import type { Panel } from './Shell';

/**
 * Błąd z serwera sprowadzony do dwóch rzeczy: co napisać i czy da się to naprawić na miejscu.
 *
 * Kod trzymamy razem z treścią, bo sam komunikat nie wystarczy — „Bez ustawień podatkowych nie
 * wiemy, jaki plan obowiązuje firmę w 2026-08" jest prawdziwy i bezużyteczny, dopóki nie prowadzi
 * tam, gdzie te ustawienia się uzupełnia.
 */
export type Failure = { message: string; code?: string };

export function failureOf(error: unknown): Failure {
  if (error instanceof ApiError) return { message: error.message, code: error.code };
  return { message: error instanceof Error ? error.message : String(error) };
}

/**
 * Błędy, które wskazują jedno konkretne miejsce do uzupełnienia.
 *
 * Wpisujemy tu tylko te, przy których wiadomo, dokąd posłać — link prowadzący „gdzieś" jest
 * gorszy niż jego brak, bo każe człowiekowi szukać drugi raz.
 */
const FIXABLE: Record<string, { panel: Panel; label: (t: Dictionary) => string }> = {
  settings_missing: { panel: 'settings', label: (t) => t.dashboard.fixSettings },
  filing_details_missing: { panel: 'settings', label: (t) => t.jpk.goToFiling },
  company_address_missing: { panel: 'settings', label: (t) => t.dashboard.fixSettings },
};

/** Pasek błędu; przy brakujących danych z przyciskiem prowadzącym tam, gdzie się je uzupełnia. */
export function ErrorAlert({
  error,
  t,
  onGo,
}: {
  error: Failure | null;
  t: Dictionary;
  onGo?: (panel: Panel) => void;
}) {
  if (!error) return null;
  const fix = error.code ? FIXABLE[error.code] : undefined;

  return (
    <div className="alert a-bad">
      <Icon.Error />
      <div>
        <b>{error.message}</b>
      </div>
      {fix && onGo && (
        <button type="button" className="btn" onClick={() => onGo(fix.panel)}>
          {fix.label(t)}
        </button>
      )}
    </div>
  );
}
